import React, { Component } from "react";
import {
    MDBRow,
    MDBCol,
    MDBNav,
    MDBNavItem,
    MDBNavLink,
    MDBTabContent,
    MDBTabPane,
    MDBIcon,
    MDBContainer,
    MDBCard,
    MDBCardBody,
    toast,
} from "mdbreact";
import SecondaryOperatorField from "./SecondaryOperatorField";
import PendingAssociationsInbox from "./PendingAssociationsInbox";
import SecondaryOperatorPermissions from "./SecondaryOperatorPermissions";
import OperatorAssociationPanel from "./OperatorAssociationPanel";
import OperatorIdDisplay from "./OperatorIdDisplay";
import "./MultiTenancy.css";

/**
 * Multi-Tenancy Prototype Showcase
 *
 * A single page that renders every prototype component in their various states
 * so you can review and iterate on the UI without wiring them into existing
 * navigation.
 *
 * To preview in the app, you can temporarily route to this component, e.g.:
 *   import MultiTenancyDemo from "./components/NetworkMgmt/MultiTenancy/MultiTenancyDemo";
 *   <Route path="/multitenancy-demo" component={MultiTenancyDemo} />
 */
class MultiTenancyDemo extends Component {
    state = {
        activeItem: "1",
        venueAssociation: { status: "NONE" },
    };

    toggleTab = (tab) => () => {
        if (this.state.activeItem !== tab) {
            this.setState({ activeItem: tab });
        }
    };

    handleRequest = (opId) => {
        this.setState({
            venueAssociation: {
                status: "PENDING",
                secondaryOperatorId: opId,
                secondaryOperatorName:
                    opId === "OP-7H4K-2891"
                        ? "Mereo Networks"
                        : opId === "OP-SMRT-AIRA"
                        ? "SmartAira"
                        : "Operator",
                requestedAt: new Date().toLocaleString(),
            },
        });
        toast.success(
            `Pending request sent. They must approve to activate access.`,
            { closeButton: false }
        );
    };

    handleCancel = () => {
        this.setState({ venueAssociation: { status: "NONE" } });
        toast.info("Request cancelled.", { closeButton: false });
    };

    handleApprove = (reqId) => {
        toast.success(`Request ${reqId} approved.`, { closeButton: false });
    };

    handleReject = (reqId) => {
        toast.warn(`Request ${reqId} rejected.`, { closeButton: false });
    };

    render() {
        const { activeItem, venueAssociation } = this.state;

        return (
            <MDBContainer fluid className="py-4">
                <MDBCard>
                    <MDBCardBody>
                        <h2>
                            <MDBIcon icon="cubes" /> Multi-Tenancy UI
                            Prototypes
                        </h2>
                        <p className="text-muted">
                            Interactive prototypes for the Secondary Operator
                            (multi-tenancy) feature set. Each tab below shows a
                            distinct piece of the workflow.
                        </p>

                        <MDBNav className="nav-tabs mt-3">
                            <MDBNavItem>
                                <MDBNavLink
                                    link
                                    to="#"
                                    active={activeItem === "1"}
                                    onClick={this.toggleTab("1")}
                                >
                                    <MDBIcon icon="building" /> 1. Venue Form
                                    Field
                                </MDBNavLink>
                            </MDBNavItem>
                            <MDBNavItem>
                                <MDBNavLink
                                    link
                                    to="#"
                                    active={activeItem === "2"}
                                    onClick={this.toggleTab("2")}
                                >
                                    <MDBIcon icon="inbox" /> 2. Inbox + 3.
                                    Approval Modal
                                </MDBNavLink>
                            </MDBNavItem>
                            <MDBNavItem>
                                <MDBNavLink
                                    link
                                    to="#"
                                    active={activeItem === "3"}
                                    onClick={this.toggleTab("3")}
                                >
                                    <MDBIcon icon="sliders-h" /> 4. Permissions
                                    Settings
                                </MDBNavLink>
                            </MDBNavItem>
                            <MDBNavItem>
                                <MDBNavLink
                                    link
                                    to="#"
                                    active={activeItem === "4"}
                                    onClick={this.toggleTab("4")}
                                >
                                    <MDBIcon icon="users-cog" /> 6. Venue
                                    Detail Panel
                                </MDBNavLink>
                            </MDBNavItem>
                            <MDBNavItem>
                                <MDBNavLink
                                    link
                                    to="#"
                                    active={activeItem === "5"}
                                    onClick={this.toggleTab("5")}
                                >
                                    <MDBIcon icon="id-card" /> 7. Operator ID
                                </MDBNavLink>
                            </MDBNavItem>
                        </MDBNav>

                        <MDBTabContent activeItem={activeItem} className="pt-3">
                            {/* Tab 1 ---------------------------------- */}
                            <MDBTabPane tabId="1">
                                <h4>Secondary Operator Field</h4>
                                <p className="text-muted">
                                    Drops into the venue create/edit form. Try
                                    entering one of:{" "}
                                    <code>OP-7H4K-2891</code>,{" "}
                                    <code>OP-SMRT-AIRA</code>, or an invalid
                                    ID.
                                </p>
                                <SecondaryOperatorField
                                    primaryOperatorId="OP-3F9X-1024"
                                    currentAssociation={venueAssociation}
                                    onRequestAssociation={this.handleRequest}
                                    onCancelRequest={this.handleCancel}
                                    onRemoveAssociation={this.handleCancel}
                                />
                            </MDBTabPane>

                            {/* Tab 2 ---------------------------------- */}
                            <MDBTabPane tabId="2">
                                <h4>Pending Associations Inbox</h4>
                                <p className="text-muted">
                                    Click <strong>Review & Respond</strong> on
                                    a card to open the approval modal with the
                                    required billing acknowledgment.
                                </p>
                                <PendingAssociationsInbox
                                    onApprove={this.handleApprove}
                                    onReject={this.handleReject}
                                />
                            </MDBTabPane>

                            {/* Tab 3 ---------------------------------- */}
                            <MDBTabPane tabId="3">
                                <h4>Secondary Operator Permissions</h4>
                                <p className="text-muted">
                                    Configure what Secondary Operators can do
                                    across venues you own.
                                </p>
                                <SecondaryOperatorPermissions
                                    operatorId="OP-3F9X-1024"
                                    operatorName="RoamingiQ Demo Managed Properties"
                                />
                            </MDBTabPane>

                            {/* Tab 4 ---------------------------------- */}
                            <MDBTabPane tabId="4">
                                <h4>Venue Detail — Operator Panels</h4>
                                <MDBRow>
                                    <MDBCol md="6">
                                        <h6 className="text-muted">
                                            Viewed as Primary Operator
                                        </h6>
                                        <OperatorAssociationPanel
                                            viewerRole="PRIMARY"
                                            primaryOperator={{
                                                id: "OP-3F9X-1024",
                                                name: "RoamingiQ Demo",
                                            }}
                                            secondaryOperator={{
                                                id: "OP-7H4K-2891",
                                                name: "Mereo Networks",
                                                status: "ACTIVE",
                                                approvedAt: "June 8, 2026",
                                                approvedBy:
                                                    "support@mereo.net",
                                            }}
                                            onRemoveSecondary={() =>
                                                toast.warn(
                                                    "Remove Secondary Operator clicked",
                                                    { closeButton: false }
                                                )
                                            }
                                            onViewPermissions={() =>
                                                toast.info(
                                                    "View Permissions clicked",
                                                    { closeButton: false }
                                                )
                                            }
                                        />
                                    </MDBCol>
                                    <MDBCol md="6">
                                        <h6 className="text-muted">
                                            Viewed as Secondary Operator
                                        </h6>
                                        <OperatorAssociationPanel
                                            viewerRole="SECONDARY"
                                            primaryOperator={{
                                                id: "OP-3F9X-1024",
                                                name: "RoamingiQ Demo",
                                            }}
                                            secondaryOperator={{
                                                id: "OP-7H4K-2891",
                                                name: "Mereo Networks",
                                                status: "ACTIVE",
                                                approvedAt: "June 8, 2026",
                                            }}
                                            viewerPermissions={{
                                                editSubscriptions: true,
                                                createResidentTickets: true,
                                                deleteSubscriptions: false,
                                                editVenueSettings: false,
                                                manageAdmins: false,
                                            }}
                                        />
                                    </MDBCol>
                                </MDBRow>
                            </MDBTabPane>

                            {/* Tab 5 ---------------------------------- */}
                            <MDBTabPane tabId="5">
                                <h4>Operator ID Widget</h4>
                                <p className="text-muted">
                                    Small reusable widget for the account
                                    settings area.
                                </p>
                                <MDBRow>
                                    <MDBCol md="6">
                                        <OperatorIdDisplay
                                            operatorId="OP-3F9X-1024"
                                            operatorName="RoamingiQ Demo Managed Properties"
                                        />
                                    </MDBCol>
                                    <MDBCol md="6">
                                        <OperatorIdDisplay
                                            operatorId="OP-7H4K-2891"
                                            operatorName="Mereo Networks"
                                            description="Used by Primary Operators to add Mereo Networks as a Secondary Operator on their venues."
                                        />
                                    </MDBCol>
                                </MDBRow>
                            </MDBTabPane>
                        </MDBTabContent>
                    </MDBCardBody>
                </MDBCard>
            </MDBContainer>
        );
    }
}

export default MultiTenancyDemo;
